<script setup lang="ts">
/**
 * SmartDateFilter - Date filter with dropdown + inputs below.
 */
import { ref, computed, watch } from 'vue'
import { DatePicker as VDatePicker } from 'v-calendar'
import { format } from 'date-fns'

const props = defineProps<{
  operator: string
  date: string
  dateEnd: string
}>()

const emit = defineEmits<{
  'update:operator': [value: string]
  'update:date': [value: string]
  'update:dateEnd': [value: string]
}>()

// UI state
const isOpen = ref(false)
const showOpDropdown = ref(false)
const showMore = ref(false)

// Local editing state
const localOp = ref('')
const localDate = ref('')
const localDateEnd = ref('')
const relativeDays = ref(7)
const relativeUnit = ref<'days' | 'weeks' | 'months'>('days')
const localYear = ref(new Date().getFullYear().toString())
const localMonth = ref('')

// Unit options for USelectMenu
const unitOptions = [
  { value: 'days', label: 'days' },
  { value: 'weeks', label: 'weeks' },
  { value: 'months', label: 'months' },
]

// Month options for in_the_month
const monthOptions = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
]

// Sync when popover opens
watch(isOpen, (open) => {
  if (open) {
    localOp.value = props.operator || ''
    localDate.value = props.date || ''
    localDateEnd.value = props.dateEnd || ''
    showOpDropdown.value = false
    // Auto-expand "More" if current operator is in that section
    showMore.value = moreOperatorOptions.some((o) => o.value === localOp.value)
    // Parse in_the_past format: "7_days"
    if (props.operator === 'in_the_past' && props.date) {
      const match = props.date.match(/^(\d+)_(days|weeks|months)$/)
      if (match) {
        relativeDays.value = parseInt(match[1], 10)
        relativeUnit.value = match[2] as 'days' | 'weeks' | 'months'
      }
    }
    // Parse in_the_year format: "2024" or in_the_month format: "2024-06"
    if (props.operator === 'in_the_year' && props.date) {
      localYear.value = props.date
      localMonth.value = ''
    } else if (props.operator === 'in_the_month' && props.date) {
      const match = props.date.match(/^(\d{4})-(\d{2})$/)
      if (match) {
        localYear.value = match[1]
        localMonth.value = match[2]
      } else {
        localYear.value = new Date().getFullYear().toString()
        localMonth.value = ''
      }
    } else {
      localYear.value = new Date().getFullYear().toString()
      localMonth.value = ''
    }
  }
})

// Main operators (always visible)
const mainOperatorOptions = [
  { value: '', label: 'is any time' },
  { value: 'in_the_past', label: 'in the past' },
  { value: 'between', label: 'between' },
  { value: 'before', label: 'before' },
  { value: 'on_or_after', label: 'on or after' },
]

// Additional operators (under "More")
const moreOperatorOptions = [
  { value: 'on_the_day', label: 'on the day' },
  { value: 'in_the_month', label: 'in the month' },
  { value: 'in_the_year', label: 'in the year' },
]

// All operators combined (for lookups)
const operatorOptions = [...mainOperatorOptions, ...moreOperatorOptions]

// Get current operator label for dropdown button
const currentOpLabel = computed(() => {
  return operatorOptions.find((o) => o.value === localOp.value)?.label || 'is any time'
})

// Display text for main button
const displayText = computed(() => {
  if (!props.operator) return 'Anytime'
  const opLabel = operatorOptions.find((o) => o.value === props.operator)?.label || props.operator
  if (props.operator === 'in_the_past' && props.date) {
    const match = props.date.match(/^(\d+)_(days|weeks|months)$/)
    if (match) return `${opLabel} ${match[1]} ${match[2]}`
  }
  if (props.operator === 'between' && props.date && props.dateEnd) {
    return `${props.date} – ${props.dateEnd}`
  }
  if (props.operator === 'in_the_month' && props.date) {
    const match = props.date.match(/^(\d{4})-(\d{2})$/)
    if (match) {
      const monthLabel = monthOptions.find((m) => m.value === match[2])?.label || match[2]
      return `${monthLabel} ${match[1]}`
    }
  }
  if (props.date) return `${opLabel} ${props.date}`
  return opLabel
})

const isActive = computed(() => !!props.operator)

// Needs full date input (YYYY-MM-DD)?
const needsDateInput = computed(() =>
  ['before', 'on_or_after', 'on_the_day'].includes(localOp.value),
)

// Date object <-> string conversions for v-calendar
const localDateObj = computed({
  get: () => (localDate.value ? new Date(localDate.value + 'T00:00:00') : null),
  set: (val: unknown) => {
    localDate.value = val instanceof Date && !isNaN(val.getTime()) ? format(val, 'yyyy-MM-dd') : ''
  },
})

// Date range object for v-calendar range mode (between operator)
const localDateRangeObj = computed({
  get: () => {
    const start = localDate.value ? new Date(localDate.value + 'T00:00:00') : null
    const end = localDateEnd.value ? new Date(localDateEnd.value + 'T00:00:00') : null
    return start && end ? { start, end } : null
  },
  set: (val: unknown) => {
    if (val && typeof val === 'object' && 'start' in val && 'end' in val) {
      const { start, end } = val as { start: unknown; end: unknown }
      const from =
        start instanceof Date && !isNaN(start.getTime()) ? format(start, 'yyyy-MM-dd') : ''
      const to = end instanceof Date && !isNaN(end.getTime()) ? format(end, 'yyyy-MM-dd') : ''
      localDate.value = from
      localDateEnd.value = to
    } else {
      localDate.value = ''
      localDateEnd.value = ''
    }
  },
})

// Highlight today in calendar with a light circle
const todayAttributes = [
  {
    key: 'today',
    highlight: {
      color: 'yellow',
      fillMode: 'outline',
    },
    dates: new Date(),
  },
]

// Select operator from dropdown
function selectOp(value: string) {
  localOp.value = value
  showOpDropdown.value = false
  showMore.value = moreOperatorOptions.some((o) => o.value === value)
  // Only clear dateEnd when switching away from between (preserve primary date)
  if (value !== 'between') localDateEnd.value = ''
}

// Apply
function applyFilter() {
  if (localOp.value === 'in_the_past') {
    const days = Number(relativeDays.value)
    if (!Number.isFinite(days) || days < 1) return
    emit('update:operator', localOp.value)
    emit('update:date', `${days}_${relativeUnit.value}`)
    emit('update:dateEnd', '')
  } else if (localOp.value === 'between') {
    if (!localDate.value || !localDateEnd.value) return
    // Swap dates if start > end
    const start = localDate.value <= localDateEnd.value ? localDate.value : localDateEnd.value
    const end = localDate.value <= localDateEnd.value ? localDateEnd.value : localDate.value
    emit('update:operator', localOp.value)
    emit('update:date', start)
    emit('update:dateEnd', end)
  } else if (localOp.value === 'in_the_year') {
    if (!localYear.value || !/^(19\d{2}|20\d{2}|21\d{2})$/.test(localYear.value)) return
    emit('update:operator', localOp.value)
    emit('update:date', localYear.value)
    emit('update:dateEnd', '')
  } else if (localOp.value === 'in_the_month') {
    if (!localYear.value || !localMonth.value) return
    emit('update:operator', localOp.value)
    emit('update:date', `${localYear.value}-${localMonth.value}`)
    emit('update:dateEnd', '')
  } else if (needsDateInput.value) {
    if (!localDate.value) return
    emit('update:operator', localOp.value)
    emit('update:date', localDate.value)
    emit('update:dateEnd', '')
  } else {
    emit('update:operator', '')
    emit('update:date', '')
    emit('update:dateEnd', '')
  }
  isOpen.value = false
}

function cancel() {
  isOpen.value = false
}
</script>

<template>
  <UPopover v-model:open="isOpen" :content="{ align: 'start' }" :ui="{ content: 'outline-none' }">
    <UButton
      :color="isActive ? 'primary' : 'neutral'"
      :variant="isActive ? 'soft' : 'outline'"
      size="sm"
      icon="i-lucide-calendar"
      trailing-icon="i-lucide-chevron-down"
      class="w-32 text-left"
    >
      <span class="truncate">{{ displayText }}</span>
    </UButton>

    <template #content>
      <div class="w-80 p-3 space-y-3 outline-none">
        <!-- Operator dropdown - custom implementation (USelectMenu doesn't work inside UPopover) -->
        <div class="relative">
          <UButton
            color="neutral"
            variant="outline"
            size="sm"
            class="w-full justify-between"
            trailing-icon="i-lucide-chevron-down"
            @click="showOpDropdown = !showOpDropdown"
          >
            {{ currentOpLabel }}
          </UButton>

          <div
            v-if="showOpDropdown"
            class="absolute top-full left-0 right-0 mt-1 bg-[var(--ui-bg)] ring ring-[var(--ui-border)] rounded-[calc(var(--ui-radius)*2)] shadow-lg z-50 p-1 max-h-64 overflow-y-auto"
          >
            <!-- Main operators -->
            <button
              v-for="op in mainOperatorOptions"
              :key="op.value"
              type="button"
              class="w-full px-2.5 py-1.5 text-left text-sm flex items-center justify-between rounded-[var(--ui-radius)] transition-colors hover:bg-[var(--ui-bg-elevated)]"
              @click="selectOp(op.value)"
            >
              <span>{{ op.label }}</span>
              <UIcon
                v-if="localOp === op.value"
                name="i-lucide-check"
                class="size-5 shrink-0 text-[var(--ui-primary)]"
              />
            </button>

            <!-- More section -->
            <button
              type="button"
              class="w-full px-2.5 py-1.5 text-left text-sm flex items-center justify-between rounded-[var(--ui-radius)] transition-colors hover:bg-[var(--ui-bg-elevated)] text-muted border-t border-[var(--ui-border)] mt-1 pt-2"
              @click.stop="showMore = !showMore"
            >
              <span>More</span>
              <UIcon
                :name="showMore ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                class="size-4 shrink-0"
              />
            </button>

            <!-- More operators (expanded) -->
            <template v-if="showMore">
              <button
                v-for="op in moreOperatorOptions"
                :key="op.value"
                type="button"
                class="w-full px-2.5 py-1.5 text-left text-sm flex items-center justify-between rounded-[var(--ui-radius)] transition-colors hover:bg-[var(--ui-bg-elevated)]"
                @click="selectOp(op.value)"
              >
                <span>{{ op.label }}</span>
                <UIcon
                  v-if="localOp === op.value"
                  name="i-lucide-check"
                  class="size-5 shrink-0 text-[var(--ui-primary)]"
                />
              </button>
            </template>
          </div>
        </div>

        <!-- Filter inputs (below dropdown) -->
        <template
          v-if="
            localOp === 'in_the_past' ||
            needsDateInput ||
            localOp === 'between' ||
            localOp === 'in_the_year' ||
            localOp === 'in_the_month'
          "
        >
          <!-- Relative time (in the past) -->
          <div v-if="localOp === 'in_the_past'" class="flex gap-2 items-center">
            <UInput
              v-model.number="relativeDays"
              type="number"
              min="1"
              max="9999"
              class="w-16"
              size="sm"
            />
            <USelectMenu
              v-model="relativeUnit"
              :items="unitOptions"
              value-key="value"
              size="sm"
              class="flex-1"
            />
          </div>

          <!-- Single date (before, on_or_after, on_the_day) -->
          <VDatePicker
            v-else-if="needsDateInput"
            :model-value="localDateObj"
            color="yellow"
            :attributes="todayAttributes"
            :popover="{ visibility: 'focus' }"
            @update:model-value="localDateObj = $event"
          >
            <template #default="{ inputValue, inputEvents }">
              <UInput
                :model-value="inputValue"
                v-on="inputEvents"
                size="sm"
                placeholder="Select date"
                class="w-full"
              />
            </template>
          </VDatePicker>

          <!-- Date range (between) - single calendar with range selection -->
          <VDatePicker
            v-else-if="localOp === 'between'"
            v-model.range="localDateRangeObj"
            color="yellow"
            :attributes="todayAttributes"
            :popover="{ visibility: 'focus' }"
          >
            <template #default="{ inputValue, inputEvents }">
              <div class="flex gap-2 items-center">
                <UInput
                  :model-value="inputValue.start"
                  v-on="inputEvents.start"
                  size="sm"
                  placeholder="Start"
                  class="flex-1"
                />
                <span class="text-muted text-sm">–</span>
                <UInput
                  :model-value="inputValue.end"
                  v-on="inputEvents.end"
                  size="sm"
                  placeholder="End"
                  class="flex-1"
                />
              </div>
            </template>
          </VDatePicker>

          <!-- Year only (in_the_year) -->
          <UInput
            v-else-if="localOp === 'in_the_year'"
            v-model="localYear"
            type="text"
            placeholder="YYYY"
            size="sm"
            class="w-full"
          />

          <!-- Month + Year (in_the_month) -->
          <div v-else-if="localOp === 'in_the_month'" class="space-y-2">
            <USelectMenu
              v-model="localMonth"
              :items="monthOptions"
              value-key="value"
              placeholder="Select month..."
              size="sm"
              class="w-full"
            />
            <UInput
              v-model="localYear"
              type="text"
              placeholder="YYYY e.g. 2023"
              size="sm"
              class="w-full"
            />
          </div>
        </template>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-2 pt-3 mt-1">
          <UButton color="neutral" variant="ghost" size="sm" @click="cancel"> Cancel </UButton>
          <UButton color="primary" size="sm" @click="applyFilter"> Update </UButton>
        </div>
      </div>
    </template>
  </UPopover>
</template>
